import { Controller } from "@hotwired/stimulus"

// Handles the "Assigned Users" panel on the workflow show page.
//
// Workflow:
//   1. User types in the search box → debounced GET to search_users endpoint.
//   2. Results appear as a click-to-assign list below the input.
//   3. Clicking a result POSTs to app_configuration_user_pairs and adds the
//      user to the "Currently assigned" list on the right.
//   4. Clicking ✕ on an assigned row sends DELETE and removes the row.

export default class extends Controller {
  static targets = [
    "searchInput",
    "searchResults",
    "searchEmpty",
    "assignedList",
    "assignedCount",
    "emptyMessage",
  ]

  static values = {
    appConfigurationId: Number,
    searchUrl:          String,   // GET  /app_configurations/search_users
    assignUrl:          String,   // POST /app_configuration_user_pairs
    removeUrl:          String,   // DELETE /app_configuration_user_pairs/:id
  }

  connect() {
    this._debounceTimer = null
  }

  // ── Search ─────────────────────────────────────────────────────────────────

  search() {
    clearTimeout(this._debounceTimer)
    const q = this.searchInputTarget.value.trim()

    if (q.length < 2) {
      this._hideResults()
      return
    }

    this._debounceTimer = setTimeout(() => this._doSearch(q), 250)
  }

  async _doSearch(q) {
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set('q', q)
      url.searchParams.set('app_configuration_id', this.appConfigurationIdValue)

      const res = await fetch(url, {
        headers: { Accept: 'application/json', 'X-CSRF-Token': this._csrf() },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const users = await res.json()
      this._renderResults(users)
    } catch (err) {
      this.searchResultsTarget.innerHTML =
        `<div class="p-2 text-danger small">Error: ${err.message}</div>`
      this.searchResultsTarget.classList.remove('d-none')
    }
  }

  _renderResults(users) {
    this.searchEmptyTarget.classList.add('d-none')

    if (users.length === 0) {
      this.searchResultsTarget.classList.add('d-none')
      this.searchEmptyTarget.classList.remove('d-none')
      return
    }

    this.searchResultsTarget.innerHTML = users.map(u => `
      <div class="d-flex justify-content-between align-items-center px-2 py-1
                  border-bottom search-result-row"
           style="cursor:pointer;"
           data-user-id="${u.id}"
           data-user-name="${this._esc(u.name)}"
           data-user-email="${this._esc(u.email)}"
           data-action="click->workflow-users#assignUser">
        <div>
          <span class="fw-semibold small">${this._esc(u.name)}</span>
          <span class="text-muted small ms-1">${this._esc(u.email)}</span>
        </div>
        <span class="badge bg-primary ms-2" style="font-size:0.7em;">+ Assign</span>
      </div>
    `).join('')

    this.searchResultsTarget.classList.remove('d-none')
  }

  _hideResults() {
    this.searchResultsTarget.classList.add('d-none')
    this.searchEmptyTarget.classList.add('d-none')
  }

  clearSearch() {
    this.searchInputTarget.value = ''
    this.searchResultsTarget.innerHTML = ''
    this._hideResults()
  }

  // ── Assign a user ──────────────────────────────────────────────────────────

  async assignUser(event) {
    const row       = event.currentTarget
    const userId    = row.dataset.userId
    const userName  = row.dataset.userName
    const userEmail = row.dataset.userEmail

    // Optimistically hide the result row
    row.style.opacity = '0.4'
    row.style.pointerEvents = 'none'

    try {
      const res = await fetch(this.assignUrlValue, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept:         'application/json',
          'X-CSRF-Token': this._csrf(),
        },
        body: JSON.stringify({
          app_configuration_id: this.appConfigurationIdValue,
          user_id:              parseInt(userId, 10),
        }),
      })

      const data = await res.json()

      if (res.ok && data.success) {
        // Add to assigned list
        this._addAssignedRow(data.pair_id, data.user_name, data.user_email)
        // Remove from search results
        row.remove()
        // Update count
        this._incrementCount(1)
      } else {
        alert(`Could not assign: ${data.error || 'Unknown error'}`)
        row.style.opacity = ''
        row.style.pointerEvents = ''
      }
    } catch (err) {
      alert(`Request failed: ${err.message}`)
      row.style.opacity = ''
      row.style.pointerEvents = ''
    }
  }

  _addAssignedRow(pairId, name, email) {
    // Remove "no users" placeholder if present
    if (this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.remove()
    }

    const div = document.createElement('div')
    div.id = `wf-user-pair-${pairId}`
    div.dataset.pairId = pairId
    div.className = 'd-flex justify-content-between align-items-center border-bottom py-1 px-1'
    div.innerHTML = `
      <div>
        <span class="fw-semibold small">${this._esc(name)}</span>
        <span class="text-muted small ms-1">${this._esc(email)}</span>
      </div>
      <button type="button"
              class="btn btn-sm btn-outline-danger py-0 px-1"
              data-action="click->workflow-users#removeUser"
              data-pair-id="${pairId}"
              title="Remove assignment">
        ✕
      </button>
    `
    this.assignedListTarget.appendChild(div)
  }

  // ── Remove an assignment ───────────────────────────────────────────────────

  async removeUser(event) {
    const pairId = event.currentTarget.dataset.pairId
    const row    = document.getElementById(`wf-user-pair-${pairId}`)

    if (!confirm('Remove this user assignment?')) return

    try {
      const res = await fetch(`${this.removeUrlValue}/${pairId}`, {
        method:  'DELETE',
        headers: {
          Accept:         'application/json',
          'X-CSRF-Token': this._csrf(),
        },
      })

      const data = await res.json()

      if (res.ok && data.success) {
        if (row) row.remove()
        this._incrementCount(-1)

        // Show empty message if no rows left
        if (this.assignedListTarget.children.length === 0) {
          const p = document.createElement('p')
          p.className = 'text-muted small'
          p.setAttribute('data-workflow-users-target', 'emptyMessage')
          p.textContent = 'No users assigned yet.'
          this.assignedListTarget.appendChild(p)
        }
      } else {
        alert(`Could not remove: ${data.error || 'Unknown error'}`)
      }
    } catch (err) {
      alert(`Request failed: ${err.message}`)
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  _incrementCount(delta) {
    if (!this.hasAssignedCountTarget) return
    const current = parseInt(this.assignedCountTarget.textContent, 10) || 0
    this.assignedCountTarget.textContent = Math.max(0, current + delta)
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }
}
